import { useEffect, useRef, useState } from 'react';

const REDUCED = '(prefers-reduced-motion: reduce)';

/** True when the user asked the OS for reduced motion. Follows the setting if it changes. */
export function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(() => typeof window !== 'undefined' && !!window.matchMedia?.(REDUCED).matches);
  useEffect(() => {
    const mq = window.matchMedia?.(REDUCED);
    if (!mq) return;
    const on = () => setReduced(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return reduced;
}

const easeOutQuint = (t: number) => 1 - Math.pow(1 - t, 5);

/**
 * Counts from 0 to `value` once, the first time a real value arrives. Later changes (refetches)
 * jump straight to the new number, so a refresh never replays the entrance.
 * Callers pass `undefined` while loading and render a skeleton, never a placeholder number.
 */
export function useCountUp(value: number | undefined, duration = 900) {
  const reduced = usePrefersReducedMotion();
  const [shown, setShown] = useState<number | undefined>(value === undefined || reduced ? value : 0);
  const played = useRef(false);
  useEffect(() => {
    if (value === undefined) return;
    if (played.current || reduced) {
      setShown(value);
      return;
    }
    played.current = true;
    let raf = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      setShown(Math.round(easeOutQuint(t) * value));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value, duration, reduced]);
  return shown;
}

/**
 * One document-level listener feeds the pointer position to whichever `.deck` is under the cursor
 * (as --mx / --my), so every hero gets the spotlight without per-component wiring. rAF-throttled;
 * does nothing for touch or reduced motion.
 */
export function useDeckSpotlight() {
  const reduced = usePrefersReducedMotion();
  useEffect(() => {
    if (reduced) return;
    let raf = 0;
    let last: PointerEvent | null = null;
    const apply = () => {
      raf = 0;
      const e = last;
      if (!e) return;
      const deck = (e.target as Element | null)?.closest?.('.deck') as HTMLElement | null;
      if (!deck) return;
      const r = deck.getBoundingClientRect();
      deck.style.setProperty('--mx', `${e.clientX - r.left}px`);
      deck.style.setProperty('--my', `${e.clientY - r.top}px`);
    };
    const onMove = (e: PointerEvent) => {
      if (e.pointerType !== 'mouse') return;
      last = e;
      if (!raf) raf = requestAnimationFrame(apply);
    };
    document.addEventListener('pointermove', onMove, { passive: true });
    return () => {
      document.removeEventListener('pointermove', onMove);
      cancelAnimationFrame(raf);
    };
  }, [reduced]);
}
